import React from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Link,
  useTheme,
  useMediaQuery,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
} from '@mui/material';
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  LinkedIn, 
  Email, 
  Phone, 
  LocationOn,
  ArrowForward,
  Star,
  TrendingUp,
  Security,
  Support
} from '@mui/icons-material';
import { BrandLogo } from '../brand/BrandLogo';
import { BRAND } from '../../config/brand';

const Footer: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      component="footer"
      sx={{
        background: `linear-gradient(135deg, ${BRAND.colors.navyDark} 0%, ${BRAND.colors.navy} 55%, #163a6b 100%)`,
        mt: 8,
        py: 8,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%)',
          pointerEvents: 'none',
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <BrandLogo height={52} variant="light" />
              <Box>
              <Typography 
                variant="h5" 
                gutterBottom 
                sx={{
                  color: BRAND.colors.cream,
                  fontWeight: 'bold',
                  mb: 0.5
                }}
              >
                {BRAND.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontStyle: 'italic' }}>
                {BRAND.tagline}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
                Pioneering the future of manufacturing with cutting-edge technology and innovative solutions.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip 
                  icon={<Star />} 
                  label="Innovation" 
                  size="small" 
                  sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    '& .MuiChip-icon': { color: 'white' }
                  }} 
                />
                <Chip 
                  icon={<TrendingUp />} 
                  label="Growth" 
                  size="small" 
                  sx={{ 
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white',
                    '& .MuiChip-icon': { color: 'white' }
                  }} 
                />
                <Chip 
                  icon={<Security />} 
                  label="Quality" 
                  size="small" 
                  sx={{ 
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    color: 'white',
                    '& .MuiChip-icon': { color: 'white' }
                  }} 
                />
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" gutterBottom sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold',
              mb: 2
            }}>
              Company
            </Typography>
            <List dense sx={{ p: 0 }}>
              {[
                { text: 'About Us', path: '/about' },
                { text: 'Services', path: '/services' },
                { text: 'Products', path: '/products' },
                { text: 'Portfolio', path: '/portfolio' },
                { text: 'Contact', path: '/contact' },
              ].map((item) => (
                <ListItem key={item.text} sx={{ p: 0, mb: 1 }}>
                  <Link 
                    href={item.path} 
                    color="inherit" 
                    underline="none"
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      opacity: 0.8,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        transform: 'translateX(5px)',
                        color: '#667eea'
                      }
                    }}
                  >
                    <ArrowForward sx={{ fontSize: 16, mr: 1 }} />
                    {item.text}
                  </Link>
                </ListItem>
              ))}
            </List>
          </Grid>

          {/* Resources */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" gutterBottom sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold',
              mb: 2
            }}>
              Resources
            </Typography>
            <List dense sx={{ p: 0 }}>
              {[
                { text: 'Blog', path: '/blog' },
                { text: 'FAQ', path: '/faq' },
                { text: 'Features', path: '/features' },
                { text: 'Pricing', path: '/pricing' },
                { text: 'Support', path: '/support' },
              ].map((item) => (
                <ListItem key={item.text} sx={{ p: 0, mb: 1 }}>
                  <Link 
                    href={item.path} 
                    color="inherit" 
                    underline="none"
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      opacity: 0.8,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        transform: 'translateX(5px)',
                        color: '#f093fb'
                      }
                    }}
                  >
                    <ArrowForward sx={{ fontSize: 16, mr: 1 }} />
                    {item.text}
                  </Link>
                </ListItem>
              ))}
            </List>
          </Grid>

          {/* Legal */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" gutterBottom sx={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold',
              mb: 2
            }}>
              Legal
            </Typography>
            <List dense sx={{ p: 0 }}>
              {[
                { text: 'Privacy Policy', path: '/privacy-policy' },
                { text: 'Terms of Service', path: '/terms-of-service' },
              ].map((item) => (
                <ListItem key={item.text} sx={{ p: 0, mb: 1 }}>
                  <Link 
                    href={item.path} 
                    color="inherit" 
                    underline="none"
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      opacity: 0.8,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        transform: 'translateX(5px)',
                        color: '#4facfe'
                      }
                    }}
                  >
                    <ArrowForward sx={{ fontSize: 16, mr: 1 }} />
                    {item.text}
                  </Link>
                </ListItem>
              ))}
            </List>
          </Grid>

          {/* Contact & Social */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" gutterBottom sx={{
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold',
              mb: 2
            }}>
              Connect
            </Typography>
            <List dense sx={{ p: 0 }}>
              <ListItem sx={{ p: 0, mb: 1 }}>
                <Link href="mailto:info@monio.com" color="inherit" underline="none" sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  opacity: 0.8,
                  transition: 'all 0.3s ease',
                  '&:hover': { opacity: 1, color: '#43e97b' }
                }}>
                  <Email sx={{ fontSize: 16, mr: 1 }} />
                  info@monio.com
                </Link>
              </ListItem>
              <ListItem sx={{ p: 0, mb: 1 }}>
                <Link href="tel:+1234567890" color="inherit" underline="none" sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  opacity: 0.8,
                  transition: 'all 0.3s ease',
                  '&:hover': { opacity: 1, color: '#43e97b' }
                }}>
                  <Phone sx={{ fontSize: 16, mr: 1 }} />
                  +1 (555) 123-4567
                </Link>
              </ListItem>
            </List>
            
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              {[
                { icon: <Facebook />, href: '#', color: '#1877f2' },
                { icon: <Twitter />, href: '#', color: '#1da1f2' },
                { icon: <Instagram />, href: '#', color: '#e4405f' },
                { icon: <LinkedIn />, href: '#', color: '#0077b5' },
              ].map((social, index) => (
                <IconButton
                  key={index}
                  href={social.href}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: social.color,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 25px ${social.color}40`
                    }
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Section */}
        <Box sx={{
          mt: 6,
          pt: 4,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center'
        }}>
          <Typography
            variant="body2"
            sx={{
              opacity: 0.7,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            © {new Date().getFullYear()} Monio Production Management. All rights reserved. 
            <br />
            <span style={{ fontSize: '0.8em' }}>
              Built with ❤️ for the future of manufacturing
            </span>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export { Footer };
